// @flow
import {
  DateTime,
  Interval,
} from 'luxon';
import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';
import {
  actionTypes,
} from 'redux-resource';

import * as actions from 'chronos-actions';
import * as selectors from 'chronos-selectors';
import {
  jiraApi,
  chronosApi,
} from 'chronos-api';

import {
  getAdditionalWorklogsForIssues,
} from './worklogs';
import {
  throwError,
} from './helpers';


/* eslint-disable */
const normalizeIssues = issues => {
  try {
    return issues.reduce((acc, issue) => {
      acc.entities.worklogs =
        (issue.fields?.worklog?.worklogs || []).reduce(
          (wacc, worklog) => {
            acc.entities.users[worklog.author.accountId || worklog.author.key] = {
              id: worklog.author.accountId || worklog.author.key,
              ...worklog.author,
            };
            wacc[worklog.id] = worklog;
            return wacc;
          },
          acc.entities.worklogs,
        )
      issue.fields.worklogs = (issue.fields?.worklog?.worklogs || []).map(w => w.id);
      // delete issue.fields.worklog;
      acc.entities.issues[issue.id] = issue;
      acc.entities.projects[issue.fields.project.id] = issue.fields.project;
      acc.result.push(issue.id);
      return acc;
    }, {
      entities: {
        issues: {},
        worklogs: {},
        users: {},
        projects: {},
      },
      result: [],
    });
  } catch (err) {
    throw err;
  }
}
/* eslint-enable */

function* buildJQLQuery() {
  const filters = yield eff.select(selectors.getAllFiltersIds);
  const isEmpty = (
    Object.keys(filters).reduce(
      (acc, filterName) => (
        acc
        && filters[filterName].length === 0
      ),
      true,
    )
  );
  const jqlFilters = yield eff.select(selectors.getResourceMap('jqlFilters'));
  const users = yield eff.select(selectors.getResourceMap('users'));
  const dateFilters = yield eff.select(selectors.getUiState('calendarDate'));
  const jql = [
    `worklogDate >= ${dateFilters.start.toFormat('y-MM-dd')}`,
    `worklogDate <= ${dateFilters.end.toFormat('y-MM-dd')}`,
    'timespent > 0',
    (
      /* eslint-disable-next-line */
      isEmpty
        ? 'worklogAuthor = currentUser()'
        : (
          filters.users.length
            ? `worklogAuthor in (${filters.users.map(u => `"${users[u]?.emailAddress || users[u]?.name || u}"`).join(',')})`
            : 'worklogAuthor != null'
        )
    ),
    (
      filters.projects.length
        && `project in (${filters.projects.join(',')})`
    ),
    (
      filters.versions.length
      && (
        `(fixVersion in (${filters.versions.join(',')}) `
        + `OR affectedVersion in (${filters.versions.join(',')}))`
      )
    ),
    (
      filters.issueTypes.length
        && `"issuetype" in (${filters.issueTypes.join(',')})`
    ),
    (
      filters.issueStatuses.length
        && `"status" in (${filters.issueStatuses.join(',')})`
    ),
    (
      filters.epics.length
        && `"epic link" in (${filters.epics.join(',')})`
    ),
    (
      filters.sprints.length
        && `Sprint in (${filters.sprints.join(',')})`
    ),
    (
      filters.components.length
        && `component in (${filters.components.join(',')})`
    ),
    (
      filters.jqlFilters.length
      && (
        filters.jqlFilters.map(id => jqlFilters[id].jql).join(',')
      )
    ),
  ].filter(f => !!f).join(' AND ');
  return jql;
}

function* fetchIssuesForBoards({
  jql,
  fields,
  boards,
}): Generator<*, *, *> {
  const boardsResponses = yield eff.all(
    boards.map(boardId => eff.call(
      jiraApi.searchForIssues,
      {
        params: {
          startAt: 0,
          jql,
          fields,
          boardId,
        },
      },
    )),
  );
  /* it needs to have a board id */
  const boardsIssues = (
    boards.map(
      (boardId, index) => ({
        boardId,
        ...boardsResponses[index],
      }),
    )
  );
  const unfinishedBoards = boardsIssues.filter(r => r.total > r.issues.length);
  const additionalIssues = yield eff.all([].concat(...unfinishedBoards.map(
    b => (
      /* test with Math.floor */
      Array.from(Array(Math.ceil(b.total / b.maxResults) - 1).keys()).map(
        i => (
          eff.call(
            jiraApi.searchForIssues,
            {
              params: {
                startAt: (i + 1) * b.maxResults,
                jql,
                fields,
                boardId: b.boardId,
              },
            },
          )
        ),
      )
    ),
  )));
  return [
    ...[].concat(...boardsResponses.map(i => i.issues)),
    ...[].concat(...additionalIssues.map(ai => ai.issues)),
  ];
}

export function* fetchIssues(debouncing): Generator<*, *, *> {
  if (debouncing) {
    yield eff.delay(500);
  }
  const resourceActions = createActionCreators(
    'read',
    {
      list: 'issues',
      mergeListIds: false,
      resourceType: 'issues',
      requestKey: 'loadingFilterOptions',
    },
  );
  try {
    yield eff.put(resourceActions.pending());
    const jql = yield eff.call(buildJQLQuery);
    const filters = yield eff.select(selectors.getAllFiltersIds);
    const { boards } = filters;
    let issues = [];
    if (boards.length) {
      issues = yield eff.call(
        fetchIssuesForBoards,
        {
          jql,
          fields: '*all',
          boards,
        },
      );
    } else {
      const response = yield eff.call(
        jiraApi.searchForIssues,
        {
          params: {
            startAt: 0,
            maxResults: 100,
            jql,
            fields: '*all',
          },
        },
      );
      const additionalIssues = (
        response.total > response.issues.length
          ? (
            yield eff.all(
              Array.from(Array(Math.ceil(response.total / response.maxResults) - 1).keys()).map(
                i => (
                  eff.call(
                    jiraApi.searchForIssues,
                    {
                      params: {
                        startAt: (i + 1) * response.maxResults,
                        maxResults: response.maxResults,
                        jql,
                        fields: '*all',
                      },
                    },
                  )),
              ),
            )
          ) : (
            []
          )
      );
      issues = [
        ...response.issues,
      ].concat(...additionalIssues.map(i => i.issues));
    }
    const incompleteIssues = issues.filter(
      issue => (
        issue.fields.worklog === undefined
        || (issue.fields?.worklog?.total || 0) > 20
      ),
    );
    if (incompleteIssues.length) {
      const additionalIssuesArr = yield eff.call(
        getAdditionalWorklogsForIssues,
        incompleteIssues,
      );
      const withAdditionalWorklogs = [
        ...issues.filter(i => (i.fields?.worklog?.total || 0) <= 20),
        ...additionalIssuesArr,
      ];
      const res = normalizeIssues(withAdditionalWorklogs);
      yield eff.put(resourceActions.succeeded({
        resources: res.result,
        includedResources: res.entities,
      }));
    } else {
      const res = normalizeIssues(issues);
      yield eff.put(resourceActions.succeeded({
        resources: res.result,
        includedResources: res.entities,
      }));
    }

    try {
      const jiraType = yield eff.select(selectors.getUiState('jiraType'));
      const allWorklogs = yield eff.select(selectors.getWorklogsDateAndIssueFiltered);
      const {worklogs: activities} = yield eff.call(
        (
          jiraType === 'server'
            ? jiraApi.getWorklogActivity
            : chronosApi.getScreenshots
        ),
        {
          body: {
            worklogIds: Object.keys(allWorklogs),
            issueId: "",
          }
        }
      );
      yield eff.put({
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resourceType: 'activities',
        resources: activities.map(a => ({...a, id: a.worklogId})),
      });
    } catch (e) {
      throwError(e);
    }
  } catch (err) {
    throwError(err);
    yield eff.put(resourceActions.succeeded({
      resources: [],
    }));
  }
}

export function* listenFetchIssues() {
  let task;
  while (true) {
    try {
      const act = yield eff.race([
        eff.take(actions.actionTypes.ADD_FILTER),
        eff.take(actions.actionTypes.REMOVE_FILTER),
        eff.take(actions.actionTypes.REFRESH_REQUEST),
      ]);
      const action = act.find(a => a);
      if (
        action.type === actions.actionTypes.ADD_FILTER
        && action.filterType === 'issueTypes'
      ) {
        const issueType = yield eff.select(selectors.getResourceItemBydId(
          'issueTypes',
          action.filterValue,
        ));
        if (issueType?.scope?.project?.id) {
          const filters = yield eff.select(s => s.filters);
          yield eff.put(actions.setFilter(
            [
              ...new Set([
                ...filters.projects,
                issueType.scope.project.id,
              ]),
            ],
            'projects',
          ));
        }
      }
      const { debouncing = true } = act.find(a => a);
      if (task) {
        yield eff.cancel(task);
      }
      task = yield eff.fork(fetchIssues, debouncing);

      const profile = yield eff.select(selectors.getProfile);
      const prevFilters = JSON.parse(localStorage.getItem('chronosFilters') || '{}');
      const filters = yield eff.select(s => s.filters);
      localStorage.setItem('chronosFilters', JSON.stringify({
        ...prevFilters,
        [profile.userData.self]: filters,
      }));
      if (action.type === actions.actionTypes.REFRESH_REQUEST) {
        const calendarDate = yield eff.select(selectors.getUiState('calendarDate'));
        const currentDate = DateTime.local();
        const currentMonth = Interval.fromDateTimes(
          currentDate.startOf('month'),
          currentDate.endOf('month'),
        );
        const currentWeek = Interval.fromDateTimes(
          currentDate.startOf('week').minus({ days: 1 }),
          currentDate.endOf('week').minus({ days: 1 }),
        );
        let chronosCalendarDate = calendarDate.toISO();
        if (
          currentMonth.toFormat('DDD') === calendarDate.toFormat('DDD')
        ) {
          chronosCalendarDate = 'currentMonth';
        }
        if (
          currentWeek.toFormat('DDD') === calendarDate.toFormat('DDD')
        ) {
          chronosCalendarDate = 'currentWeek';
        }
        if (
          currentDate.toFormat('DDD') === calendarDate.toFormat('DDD')
        ) {
          chronosCalendarDate = 'currentDay';
        }
        localStorage.setItem(
          'chronosCalendarDate',
          chronosCalendarDate,
        );
      }
    } catch (err) {
      throwError(err);
    }
  }
}

export function* loadSelectIssuesOptionsRequest({
  query,
  loadedQty,
  resolve,
}) {
  try {
    const projects = yield eff.select(selectors.getResourceMappedList('projects', 'all'));
    const jql = (
      Number.isNaN(query)
        ? (
          `issuekey in (${projects.map(p => `${p.key}-${query}`)})`
        ) : (
          `text ~ "${query}*"`
        )
    );
    const response = yield eff.call(
      jiraApi.searchForIssues,
      {
        params: {
          startAt: 0,
          maxResults: 500,
          jql,
          fields: '*all',
        },
      },
    );
    const {
      issues,
      total,
    } = response;
    resolve({
      options: (
        issues.map(
          issue => ({
            value: issue.id,
            label: (
              `${issue.key} - ${issue.fields.summary}`
            ),
            issue,
          }),
        )
      ),
      hasMore: (
        loadedQty + issues.length !== total
      ),
    });
  } catch (err) {
    throwError(err);
  }
}

export function* takeLoadSelectIssuesOptionsRequest() {
  yield eff.takeEvery(
    actions.actionTypes.LOAD_SELECT_ISSUES_REQUEST,
    loadSelectIssuesOptionsRequest,
  );
}
